<template>
  <div class="pp-widget" :class="{ 'is-busy': !!pending }">
    <div class="pp-widget__top">
      <select :value="nodeId" class="form-control form-control-sm" :disabled="!!pending" @change="onNodeChange">
        <option value="">-- Node --</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
      </select>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Refresh"
        :disabled="!nodeId || !!pending"
        @click="refreshAll"
      >
        <i class="fas" :class="pending === 'refresh' ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
      </button>
    </div>

    <div class="pp-widget__transport">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        :disabled="!nodeId || !!pending"
        @click="run('prev', () => cmd('triggerPrevious'))"
      >
        <i class="fas fa-step-backward" /> Prev
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="!nodeId || !!pending"
        @click="run('next', () => cmd('triggerNext'))"
      >
        Next <i class="fas fa-step-forward" />
      </button>
      <button
        type="button"
        class="btn btn-sm btn-warning"
        :disabled="!nodeId || !!pending"
        @click="run('clear', () => cmd('clear'))"
      >
        Clear
      </button>
    </div>

    <div class="pp-widget__section">
      <div class="pp-widget__section-title">
        <span>Looks</span>
        <span v-if="pending === 'refresh'" class="pp-widget__hint"><i class="fas fa-spinner fa-spin" /></span>
      </div>
      <div v-if="!nodeId" class="pp-widget__empty">Select a node</div>
      <div v-else-if="!looks.length && pending !== 'refresh'" class="pp-widget__empty">
        No looks — enable REST API (useREST) on connector for ProPresenter 7.9+
      </div>
      <div v-else class="pp-chip-grid">
        <button
          v-for="l in looks"
          :key="l.id || l.name"
          type="button"
          class="pp-chip"
          :class="{
            'is-active': l.id === currentLookId,
            'is-pending': pending === `look:${l.id || l.name}`,
          }"
          :disabled="!!pending"
          :title="l.name"
          @click="triggerLook(l)"
        >
          {{ l.name }}
        </button>
      </div>
    </div>

    <div class="pp-widget__section">
      <div class="pp-widget__section-title">
        <span>Playlist</span>
      </div>
      <select
        v-model="selectedPlaylistId"
        class="form-control form-control-sm mb-2"
        :disabled="!nodeId || !!pending || !playlists.length"
        @change="onPlaylistChange"
      >
        <option value="">-- Playlist --</option>
        <option v-for="p in playlists" :key="p.id" :value="p.id">{{ p.name }}</option>
      </select>
      <div v-if="selectedPlaylistId && !playlistItems.length && pending !== 'playlist'" class="pp-widget__empty">
        No items
      </div>
      <div v-else-if="selectedPlaylistId" class="pp-item-list">
        <button
          v-for="item in playlistItems"
          :key="`${item.index}-${item.id || item.name}`"
          type="button"
          class="pp-item"
          :class="{ 'is-pending': pending === `item:${item.index}` }"
          :disabled="!!pending"
          @click="triggerItem(item)"
        >
          <i class="fas fa-play pp-item__icon" />
          <span class="pp-item__name">{{ item.name }}</span>
        </button>
      </div>
    </div>

    <div v-if="stageLayouts.length" class="pp-widget__section">
      <div class="pp-widget__section-title">
        <span>Stage layout</span>
      </div>
      <select
        v-if="stageScreens.length > 1"
        v-model="selectedScreenId"
        class="form-control form-control-sm mb-2"
        :disabled="!!pending"
      >
        <option v-for="s in stageScreens" :key="s.id" :value="s.id">{{ s.name || s.id }}</option>
      </select>
      <div class="pp-chip-grid">
        <button
          v-for="lay in stageLayouts"
          :key="lay.id || lay.name"
          type="button"
          class="pp-chip pp-chip--stage"
          :class="{ 'is-pending': pending === `stage:${lay.id}` }"
          :disabled="!!pending || !effectiveScreenId"
          @click="setStageLayout(lay)"
        >
          {{ lay.name }}
        </button>
      </div>
    </div>

    <div v-if="error" class="pp-widget__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
  },
  data() {
    return {
      looks: [],
      currentLookId: '',
      playlists: [],
      selectedPlaylistId: '',
      playlistItems: [],
      stageLayouts: [],
      stageScreens: [],
      selectedScreenId: '',
      pending: '',
      error: '',
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    nodeId() {
      return this.config.nodeId || ''
    },
    effectiveScreenId() {
      return this.selectedScreenId || (this.stageScreens[0] && this.stageScreens[0].id) || ''
    },
  },
  watch: {
    nodeId: {
      immediate: true,
      handler(id) {
        this.looks = []
        this.currentLookId = ''
        this.playlists = []
        this.selectedPlaylistId = this.config.playlistId || ''
        this.playlistItems = []
        this.stageLayouts = []
        this.stageScreens = []
        this.selectedScreenId = ''
        this.error = ''
        this.pending = ''
        if (id) this.refreshAll()
      },
    },
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    onNodeChange(e) {
      if (!this.widgetId) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { nodeId: e.target.value } })
    },
    onPlaylistChange() {
      if (this.widgetId) {
        this.updateWidgetConfig({
          widgetId: this.widgetId,
          config: { playlistId: this.selectedPlaylistId },
        })
      }
      if (this.selectedPlaylistId) this.loadPlaylistItems()
      else this.playlistItems = []
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          return null
        }
      }
      return payload
    },
    async cmd(action, payload = {}) {
      if (!this.nodeId) return null
      const res = await this.sendCommand({
        node_id: this.nodeId,
        adapter: 'propresenter',
        action,
        payload,
      })
      return this.parsePayload(res)
    },
    async run(key, fn) {
      if (this.pending) return
      this.pending = key
      this.error = ''
      try {
        await fn()
      } catch (e) {
        this.error = e.message || 'Command failed'
      } finally {
        this.pending = ''
      }
    },
    async refreshAll() {
      if (!this.nodeId || this.pending) return
      this.pending = 'refresh'
      this.error = ''
      try {
        await Promise.all([this.loadLooks(), this.loadPlaylists(), this.loadStage()])
        if (this.selectedPlaylistId) await this.loadPlaylistItems()
      } catch (e) {
        this.error = e.message || 'Refresh failed'
      } finally {
        this.pending = ''
      }
    },
    async loadLooks() {
      const data = await this.cmd('listLooks')
      this.looks = Array.isArray(data?.looks) ? data.looks : []
      this.currentLookId = data?.currentId || ''
    },
    async loadPlaylists() {
      const data = await this.cmd('listPlaylists')
      this.playlists = Array.isArray(data?.playlists) ? data.playlists : []
      if (this.selectedPlaylistId && !this.playlists.some((p) => p.id === this.selectedPlaylistId)) {
        this.selectedPlaylistId = ''
        this.playlistItems = []
      }
      if (!this.selectedPlaylistId && this.playlists.length === 1) {
        this.selectedPlaylistId = this.playlists[0].id
        if (this.widgetId) {
          this.updateWidgetConfig({
            widgetId: this.widgetId,
            config: { playlistId: this.selectedPlaylistId },
          })
        }
      }
    },
    async loadPlaylistItems() {
      if (!this.selectedPlaylistId) {
        this.playlistItems = []
        return
      }
      const wasPending = this.pending
      if (!wasPending) this.pending = 'playlist'
      try {
        const data = await this.cmd('getPlaylist', { id: this.selectedPlaylistId })
        this.playlistItems = Array.isArray(data?.items) ? data.items : []
      } finally {
        if (!wasPending) this.pending = ''
      }
    },
    async loadStage() {
      try {
        const data = await this.cmd('listStageLayouts')
        this.stageLayouts = Array.isArray(data?.layouts) ? data.layouts : []
        this.stageScreens = Array.isArray(data?.screens) ? data.screens : []
        if (!this.selectedScreenId && this.stageScreens.length) {
          this.selectedScreenId = this.stageScreens[0].id
        }
      } catch (e) {
        this.stageLayouts = []
        this.stageScreens = []
      }
    },
    triggerLook(look) {
      const key = `look:${look.id || look.name}`
      this.run(key, async () => {
        await this.cmd('triggerLook', { id: look.id, name: look.name })
        this.currentLookId = look.id || this.currentLookId
      })
    },
    triggerItem(item) {
      this.run(`item:${item.index}`, async () => {
        await this.cmd('triggerPlaylistItem', {
          playlistId: this.selectedPlaylistId,
          index: item.index,
          presentationPath: item.id || undefined,
        })
      })
    },
    setStageLayout(lay) {
      this.run(`stage:${lay.id}`, async () => {
        await this.cmd('setStageLayout', {
          screenId: this.effectiveScreenId,
          layoutId: lay.id,
        })
      })
    },
  },
}
</script>

<style scoped>
.pp-widget {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  height: 100%;
  min-height: 0;
  color: var(--ws-text);
  overflow: auto;
}
.pp-widget__top {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}
.pp-widget__top select {
  flex: 1;
  min-width: 0;
}
.pp-widget__transport {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.pp-widget__section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ws-text-muted, #6c757d);
  margin-bottom: 0.35rem;
}
.pp-widget__hint {
  font-size: 0.7rem;
  opacity: 0.8;
}
.pp-widget__empty {
  font-size: 0.78rem;
  color: var(--ws-text-muted, #6c757d);
  padding: 0.35rem 0;
}
.pp-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.pp-chip {
  border: 1px solid var(--ws-widget-border, #ced4da);
  background: var(--ws-widget-bg, #fff);
  color: var(--ws-text);
  border-radius: 6px;
  padding: 0.35rem 0.55rem;
  font-size: 0.78rem;
  line-height: 1.2;
  cursor: pointer;
  max-width: 100%;
}
.pp-chip:hover:not(:disabled) {
  border-color: var(--ws-accent, #128c7e);
}
.pp-chip.is-active {
  background: var(--ws-accent, #128c7e);
  border-color: var(--ws-accent, #128c7e);
  color: #fff;
}
.pp-chip.is-pending,
.pp-item.is-pending {
  opacity: 0.65;
}
.pp-chip:disabled,
.pp-item:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.pp-chip--stage {
  border-style: dashed;
}
.pp-item-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 180px;
  overflow: auto;
}
.pp-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  border: 1px solid var(--ws-widget-border, #e9ecef);
  background: var(--ws-widget-bg, #fff);
  color: var(--ws-text);
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
}
.pp-item:hover:not(:disabled) {
  border-color: var(--ws-accent, #128c7e);
}
.pp-item__icon {
  color: var(--ws-accent, #128c7e);
  font-size: 0.7rem;
  flex-shrink: 0;
}
.pp-item__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-widget__error {
  font-size: 0.75rem;
  color: #dc3545;
}
</style>
